import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Stack, TextField, Tooltip, Typography } from "@mui/material";
import ColorScalePicker from "./ColorScalePicker";
import type { AxisProperties } from "./api";
import t from "./translations";

function LabelWithHint({ label, hint }: { label: string; hint: string }) {
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Tooltip title={hint}>
        <InfoOutlinedIcon
          fontSize="inherit"
          sx={{ color: "text.secondary" }}
        />
      </Tooltip>
    </Stack>
  );
}

const VALUE_MIN = 0;
const VALUE_MAX = 255;

const clampValue = (n: number) => Math.min(VALUE_MAX, Math.max(VALUE_MIN, n));

const SEVERITY_ROWS = [
  {
    key: "1",
    thresholdKey: "threshold_1",
    label: t.SeverityCriticalLabel,
    upper: (v: AxisProperties) => v.max_value,
  },
  {
    key: "2",
    thresholdKey: "threshold_2",
    label: t.SeverityIssueLabel,
    upper: (v: AxisProperties) => v.threshold_1 - 1,
  },
  {
    key: "3",
    thresholdKey: "threshold_3",
    label: t.SeverityShouldBeFixedLabel,
    upper: (v: AxisProperties) => v.threshold_2 - 1,
  },
  {
    key: "4",
    thresholdKey: "threshold_4",
    label: t.SeverityGoodToKnowLabel,
    upper: (v: AxisProperties) => v.threshold_3 - 1,
  },
] as const;

type Props = {
  region: string;
  value: AxisProperties;
  onChange: (value: AxisProperties) => void;
  disabled?: boolean;
};

// Pure field layout, shared by CreateAxisDialog and AxisDetailsDrawer - each
// owns its own state/save logic, this just renders the shared fields once.
// name_en is deliberately NOT here - callers render it themselves so the
// drawer can put its Save affordance right next to it, same as the
// Systems tab's edit drawer.
export default function AxisPropertiesFields({
  region,
  value,
  onChange,
  disabled,
}: Props) {
  const set = <K extends keyof AxisProperties>(
    key: K,
    fieldValue: AxisProperties[K]
  ) => onChange({ ...value, [key]: fieldValue });

  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <LabelWithHint label={t.ValueRangeLabel} hint={t.ValueRangeHint} />
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Tooltip title={t.MinValueLockedHint}>
            <span style={{ width: "100%" }}>
              <TextField
                label={t.MinValueLabel}
                type="number"
                value={value.min_value}
                disabled
                slotProps={{ htmlInput: { min: VALUE_MIN, max: VALUE_MAX } }}
                fullWidth
              />
            </span>
          </Tooltip>
          <Typography color="text.secondary">-</Typography>
          <TextField
            label={t.MaxValueLabel}
            type="number"
            value={value.max_value}
            onChange={(event) => {
              const max = clampValue(Number(event.target.value));
              set("max_value", Math.max(max, value.min_value));
            }}
            disabled={disabled}
            slotProps={{ htmlInput: { min: VALUE_MIN, max: VALUE_MAX } }}
            fullWidth
          />
        </Stack>
      </Stack>
      <Stack spacing={0.5}>
        <LabelWithHint label={t.ColorScaleLabel} hint={t.ColorScaleHint} />
        <ColorScalePicker
          region={region}
          min={value.min_value}
          max={value.max_value}
          value={value.color_scale}
          onChange={(colorScale) =>
            colorScale !== null && set("color_scale", colorScale)
          }
        />
      </Stack>
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {t.ThresholdsLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            — {t.ThresholdsDescription}
          </Typography>
        </Stack>
        <Stack spacing={0.5}>
          {SEVERITY_ROWS.map((row) => {
            const lower = value[row.thresholdKey];
            const upper = row.upper(value);
            const invalid = lower > upper;
            return (
              <Stack
                key={row.key}
                direction="row"
                spacing={0.5}
                sx={{ alignItems: "center" }}
              >
                <TextField
                  variant="standard"
                  type="number"
                  size="small"
                  value={lower}
                  onChange={(event) =>
                    set(row.thresholdKey, Number(event.target.value))
                  }
                  error={invalid}
                  disabled={disabled}
                  sx={{ width: 44 }}
                  slotProps={{ htmlInput: { min: VALUE_MIN, max: VALUE_MAX } }}
                />
                <Typography
                  variant="caption"
                  color={invalid ? "error" : "text.secondary"}
                >
                  {invalid
                    ? t.SeverityRangeInvalidSuffix(row.label)
                    : t.SeverityRangeSuffix(upper, row.label)}
                </Typography>
              </Stack>
            );
          })}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ pl: "50px" }}
          >
            {t.SeverityRangeLabel(
              value.min_value,
              value.threshold_4 - 1,
              t.SeverityNoIssueLabel
            )}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}
